import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Form,
    FormGroup,
    Label,
    Input
} from 'reactstrap';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import { AudioRecorder } from '../AudioRecorder/AudioRecorder';
import FileUploader from '../FileUploader/FileUploader';
import './SupportModal.css';
import { useNotification } from '../../components/Notification/NotificationContext';
import { useAuth } from '../../components/Auth/AuthContext';

const SupportModal = ({
    isOpen,
    toggle,
    senderNameMaxLength,
    senderEmailMaxLength,
    subject,
    customSubject,
    customSubjectMaxLength,
    onSubjectChange,
    message,
    messageMaxLength,
    onMessageChange,
    onSubmit,
    onCancel,
    files,
    onFileChange,
    onRemoveFile,
    onClearFiles,
    totalFileSize,
    maxTotalSize,
    isLoading
}) => {
    const { isAuthenticated } = useAuth();
    const [showConfirmClose, setShowConfirmClose] = useState(false);
    const [senderName, setSenderName] = useState('');
    const [senderEmail, setSenderEmail] = useState('');
    const [lastActiveFieldId, setLastActiveFieldId] = useState('message');
    const { showWarning } = useNotification();

    const handleFieldFocus = (e) => {
        setLastActiveFieldId(e.target.id);
    };

    const handleMessageChange = (e) => {
        const { id, value } = e.target;

        if (id === 'message') {
            onMessageChange({ target: { value } });
        } else if (id === 'custom-subject') {
            onSubjectChange({ target: { value } });
        } else if (id === 'sender-name') {
            setSenderName(value);
        } else if (id === 'sender-mail') {
            setSenderEmail(value);
        }

        setLastActiveFieldId(id);
    };

    const hasUnsavedChanges = () => {
        return (
            (customSubject && customSubject.trim().length > 0) ||
            (message && message.trim().length > 0) ||
            (files && files.length > 0) ||
            (!isAuthenticated && senderName && senderName.trim().length > 0) ||
            (!isAuthenticated && senderEmail && senderEmail.trim().length > 0)
        );
    };

    const handleToggle = () => {
        if (hasUnsavedChanges()) {
            setShowConfirmClose(true);
        } else {
            toggle();
        }
    };

    const handleCancel = () => {
        onCancel();
    };

    const handleConfirmClose = () => {
        setSenderName('');
        setSenderEmail('');
        setShowConfirmClose(false);
        onCancel();
    };

    const handleCancelClose = () => {
        setShowConfirmClose(false);
    };

    const handleCancelButtonClick = () => {
        if (hasUnsavedChanges()) {
            setShowConfirmClose(true);
        } else {
            handleCancel();
        }
    };

    const handleSubmit = (e) => {
        if (!isAuthenticated) {
            if (!senderName.trim()) {
                showWarning('Пожалуйста, укажите Ваше ФИО');
                return;
            }
            if (!senderEmail.trim()) {
                showWarning('Пожалуйста, укажите Ваш email');
                return;
            }
        }
        onSubmit(e, isAuthenticated ? null : senderName.trim(), isAuthenticated ? null : senderEmail.trim());
    };

    useEffect(() => {
        if (isOpen) {
            const fieldToFocus = document.getElementById(lastActiveFieldId) || document.getElementById('message');
            if (fieldToFocus) {
                fieldToFocus.focus();
            }
        }
    }, [isOpen, lastActiveFieldId]);

    return (
        <>
            <Modal size="lg" isOpen={isOpen} toggle={handleToggle} className="support-modal">
                <ModalHeader toggle={handleToggle}>
                    Обращение в техподдержку
                </ModalHeader>
                <ModalBody>
                    <Form>
                        {!isAuthenticated && (
                            <FormGroup>
                                <Label for="sender-name">Отправитель</Label>
                                <Input
                                    className="text-input audio-recorder-target"
                                    type="text"
                                    id="sender-name"
                                    name="sender-name"
                                    placeholder="Введите Ваше полное имя"
                                    value={senderName}
                                    onChange={handleMessageChange}
                                    disabled={isLoading}
                                    onFocus={handleFieldFocus}
                                    autoComplete="name"
                                    required
                                    maxLength={senderNameMaxLength}
                                />
                                <div className={`counter mb-1 ${senderName.length > senderNameMaxLength ? 'red-color' : ''}`}>
                                    {senderName.length}/{senderNameMaxLength}
                                </div>
                                <Input
                                    className="text-input audio-recorder-target"
                                    type="mail"
                                    id="sender-mail"
                                    name="sender-mail"
                                    placeholder="Введите Ваш email"
                                    value={senderEmail}
                                    onChange={handleMessageChange}
                                    disabled={isLoading}
                                    onFocus={handleFieldFocus}
                                    autoComplete="email"
                                    required
                                    maxLength={senderEmailMaxLength}
                                />
                                <div className={`counter ${senderEmail.length > senderEmailMaxLength ? 'red-color' : ''}`}>
                                    {senderEmail.length}/{senderEmailMaxLength}
                                </div>
                            </FormGroup>
                        )}
                        <FormGroup className="subject-group">
                            <Label for="custom-subject">Тема</Label>
                            <div
                                id="subject"
                                className="support-modal-subject">
                                {subject || ''}
                            </div>
                            <Input
                                className={`text-input audio-recorder-target`}
                                type="text"
                                id="custom-subject"
                                name="custom-subject"
                                placeholder="Дополните тему при необходимости"
                                value={customSubject}
                                onChange={handleMessageChange}
                                disabled={isLoading}
                                maxLength={customSubjectMaxLength}
                                onFocus={handleFieldFocus}
                                autoComplete="off" />
                            <div className={`counter ${customSubject.length > customSubjectMaxLength ? 'red-color' : ''}`}>
                                {customSubject.length}/{customSubjectMaxLength}
                            </div>
                        </FormGroup>
                        <FormGroup>
                            <Label for="message">Сообщение</Label>
                            <AudioRecorder
                                onMessageChange={handleMessageChange}
                                messageMaxLength={messageMaxLength}
                                isLoading={isLoading}
                                defaultTargetFieldId="message"
                            />
                            <Input
                                className="text-input audio-recorder-target"
                                type="textarea"
                                id="message"
                                value={message}
                                onChange={handleMessageChange}
                                onFocus={handleFieldFocus}
                                rows="5"
                                placeholder="Опишите вашу проблему или вопрос..."
                                disabled={isLoading} />
                            <div className={`counter ${message.length > messageMaxLength ? 'red-color' : ''}`}>
                                {message.length}/{messageMaxLength}
                            </div>
                        </FormGroup>
                        <FormGroup>
                            <FileUploader
                                files={files}
                                onFileChange={onFileChange}
                                onRemoveFile={onRemoveFile}
                                onClearFiles={onClearFiles}
                                totalFileSize={totalFileSize}
                                maxTotalSize={maxTotalSize}
                                isLoading={isLoading}
                                multiple={true}
                                accept=".jpg,.jpeg,.png"
                                label="Загрузить файл"
                            />
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button
                        className="dt1520-button active-dt1520-button view-button blue-button-with-borders"
                        onClick={handleSubmit}
                        disabled={isLoading}>
                        {isLoading ? 'Отправка...' : 'Отправить'}
                    </Button>
                    <Button
                        className="dt1520-button active-dt1520-button view-button default-button"
                        onClick={handleCancelButtonClick}
                        disabled={isLoading}>
                        Отменить
                    </Button>
                </ModalFooter>
            </Modal>

            <ConfirmDialog
                isOpen={showConfirmClose}
                message="Вы не отправили письмо. Уверены, что хотите закрыть окно?"
                confirmText="Закрыть"
                cancelText="Отмена"
                onConfirm={handleConfirmClose}
                onCancel={handleCancelClose}
                onClose={handleCancelClose}
            />
        </>
    );
}

export default SupportModal;